import React, {Fragment, useEffect, useMemo, useRef, useState} from "react";
import {
    Dialog,
    DialogPanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Transition,
    TransitionChild
} from "@headlessui/react";
import {
    ArchiveBoxIcon,
    ArrowDownTrayIcon,
    BanknotesIcon,
    Bars3Icon,
    BuildingOffice2Icon,
    BuildingStorefrontIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ClipboardDocumentListIcon,
    CreditCardIcon,
    EnvelopeIcon,
    GiftIcon,
    GlobeAltIcon,
    MegaphoneIcon,
    HomeIcon,
    IdentificationIcon,
    SparklesIcon,
    ListBulletIcon,
    ShieldCheckIcon,
    Squares2X2Icon,
    SwatchIcon,
    TagIcon,
    UserGroupIcon,
    UsersIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import {Link, Navigate, NavLink, Outlet, useLocation, useNavigate} from "react-router-dom";
import {useAuth} from "../context/AuthContext";

import logo from "../assets/logo_alt.png";
import {BarChart3Icon, MessageSquareIcon, ShoppingBagIcon, TicketIcon, Trophy} from "lucide-react";
import {ChevronDownIcon} from "@heroicons/react/16/solid";
import {useBusiness} from "../context/BusinessContext";
import {useBusinessAccess} from "../hooks/useBusinessAccess";
import {PERMISSIONS, ROLES} from "../lib/roles";
import BackToTop from "../components/BackToTop";
import {
    isWelcomeDismissed,
    markWelcomeSeen,
    useOnboardingStatus,
    wasWelcomeSeen,
} from "../features/business/features/welcome/useOnboardingStatus";

const ADMIN_ONLY = [ROLES.ADMIN, ROLES.BUSINESS];
// Visible to business admins (by role) or employees granted the SALES permission at
// this business. Both are required for an employee: the role alone is not enough.
const SALES_ACCESS = {roles: ADMIN_ONLY, permissions: [PERMISSIONS.SALES]};

type LeafNavItem = {
    type?: "link";
    name: string;
    to: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    end?: boolean;
    roles?: string[];
    permissions?: string[];
    badge?: React.ReactNode;
};

type GroupNavItem = {
    type: "group";
    name: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    children: LeafNavItem[];
    roles?: string[];
    permissions?: string[];
};

type NavItem = LeafNavItem | GroupNavItem;

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

const navLinkClass = (isActive: boolean, collapsed = false) =>
    cx(
        isActive ? "bg-white/10 text-white" : "text-gray-300 hover:bg-white/5 hover:text-white",
        "group flex rounded-md p-2 text-sm/6 font-semibold",
        collapsed ? "justify-center" : "gap-x-3"
    );

function isGroup(item: NavItem): item is GroupNavItem {
    return item.type === "group";
}

export default function BusinessLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const {user, logout, isReady: authReady} = useAuth();
    const {businesses, activeBusiness, selectBusiness, isReady: bizReady} = useBusiness();

    // Scoped to the active business: account-wide roles are a union across every
    // business the user belongs to and would over-report access here.
    const {roles: effectiveRoles, isAdmin: isBusinessAdmin, canAccess} = useBusinessAccess();

    const onboarding = useOnboardingStatus(isBusinessAdmin ? activeBusiness?.id : undefined);
    const dismissed = isWelcomeDismissed(activeBusiness?.id);
    const showOnboarding =
        isBusinessAdmin &&
        !!activeBusiness?.id &&
        !onboarding.isLoading &&
        !onboarding.isComplete &&
        !dismissed;

    // One-shot redirect for first-time admins landing on the dashboard.
    const redirectedRef = useRef<string | null>(null);
    useEffect(() => {
        const bid = activeBusiness?.id;
        if (!bid) return;
        if (!isBusinessAdmin) return;
        if (onboarding.isLoading) return;
        if (onboarding.isComplete) return;
        if (dismissed) return;
        if (wasWelcomeSeen(bid)) return;
        if (redirectedRef.current === bid) return;

        const path = location.pathname;
        const onDashboard = path === "/business" || path === "/business/dashboard";
        if (!onDashboard) return;

        redirectedRef.current = bid;
        markWelcomeSeen(bid);
        navigate("/business/welcome", {replace: true});
    }, [
        activeBusiness?.id,
        isBusinessAdmin,
        onboarding.isLoading,
        onboarding.isComplete,
        dismissed,
        location.pathname,
        navigate,
    ]);

    const navigation: NavItem[] = useMemo(
        () => [
            // Onboarding (only while incomplete & not dismissed; admin-only)
            ...(showOnboarding
                ? ([
                    {
                        name: "Getting Started",
                        to: "/business/welcome",
                        icon: SparklesIcon,
                        end: true,
                        roles: ADMIN_ONLY,
                        badge: (
                            <span
                                className="ml-auto inline-flex items-center rounded-full bg-orange-500/20 px-2 py-0.5 text-xs font-semibold text-orange-200">
                                {onboarding.completedCount}/{onboarding.total}
                            </span>
                        ),
                    },
                ] as NavItem[])
                : []),

            // Overview
            {name: "Dashboard", to: "/business/dashboard", icon: HomeIcon, end: true, ...SALES_ACCESS},

            // Core business
            {
                type: "group",
                name: "Business Profile",
                icon: BuildingStorefrontIcon,
                roles: ADMIN_ONLY,
                children: [
                    {name: "Details", to: "/business/profile/details", icon: ClipboardDocumentListIcon, end: true},
                    {name: "Branding", to: "/business/profile/branding", icon: SwatchIcon},
                    {name: "Social Media", to: "/business/profile/social-media", icon: GlobeAltIcon},
                    {name: "Advert Tracking", to: "/business/profile/meta-pixel", icon: MegaphoneIcon},
                    {name: "Banking Details", to: "/business/profile/banking", icon: BanknotesIcon},
                    {name: "Invoice Emails", to: "/business/profile/invoice-emails", icon: EnvelopeIcon},
                ],
            },

            {name: "Downloads", to: "/business/downloads", icon: ArrowDownTrayIcon, roles: ADMIN_ONLY},

            // Events hub (event, tournaments, orders & feedback grouped together)
            {
                type: "group",
                name: "Events",
                icon: TicketIcon,
                ...SALES_ACCESS,
                children: [
                    {name: "All Events", to: "/business/events", icon: TicketIcon, end: true, roles: ADMIN_ONLY},
                    {name: "Tournaments", to: "/business/tournaments", icon: Trophy, roles: ADMIN_ONLY},
                    {name: "Orders", to: "/business/orders", icon: ShoppingBagIcon, ...SALES_ACCESS},
                    {name: "Feedback", to: "/business/feedback", icon: MessageSquareIcon, roles: ADMIN_ONLY},
                    // Admin only: running a draw requires business admin on the API.
                    {name: "Lucky Draws", to: "/business/lucky-draws", icon: GiftIcon, roles: ADMIN_ONLY},
                ],
            },
            {name: "Customers", to: "/business/customers", icon: UsersIcon, roles: ADMIN_ONLY},

            // Memberships (Programs)
            {
                type: "group",
                name: "Memberships",
                icon: IdentificationIcon,
                roles: ADMIN_ONLY,
                children: [
                    {
                        name: "Programs",
                        to: "/business/memberships/programs",
                        icon: ListBulletIcon,
                        end: true,
                    },
                    {name: "Members", to: "/business/memberships/members", icon: UserGroupIcon},
                    {name: "Statistics", to: "/business/memberships/statistics", icon: BarChart3Icon},
                ],
            },

            // Cashless (Bar & inventory)
            {
                type: "group",
                name: "Cashless",
                icon: CreditCardIcon,
                roles: ADMIN_ONLY,
                children: [
                    {name: "Transactions", to: "/business/cashless/transactions", icon: BanknotesIcon},
                    {name: "Vendors", to: "/business/cashless/vendors", icon: BuildingOffice2Icon},
                    {name: "Categories & Items", to: "/business/cashless/menu-categories", icon: TagIcon},
                    {name: "Specials", to: "/business/cashless/specials", icon: SparklesIcon},
                    {name: "Stock Overview", to: "/business/cashless/dashboard", icon: Squares2X2Icon, end: true},
                    {name: "Stock Management", to: "/business/cashless/stock-items", icon: ArchiveBoxIcon},
                ],
            },

            {name: "Team", to: "/business/users", icon: ShieldCheckIcon, roles: ADMIN_ONLY},

            // Comms. Admin only throughout: composing, branding and sending all require business
            // admin on the API, so an employee would see a screen they could only read.
            {
                type: "group",
                name: "Mailing",
                icon: EnvelopeIcon,
                roles: ADMIN_ONLY,
                children: [
                    {name: "Mailings", to: "/business/mailing", icon: EnvelopeIcon, end: true},
                    {name: "Branding", to: "/business/mailing/branding", icon: SwatchIcon},
                ],
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [showOnboarding, onboarding.completedCount, onboarding.total]
    );

    const visibleNavigation = useMemo(() => {
        // Admins get in by role. Employees need the matching permission granted at this
        // business, so a permission on its own never opens an item.
        const canSee = (roles?: string[], permissions?: string[]) => {
            if (!roles && !permissions) return true;
            if (roles?.some((r) => effectiveRoles.has(r))) return true;
            return permissions ? canAccess(...permissions) : false;
        };
        return navigation
            .filter((item) => canSee(item.roles, item.permissions))
            .map((item) =>
                isGroup(item)
                    ? {...item, children: item.children.filter((c) => canSee(c.roles, c.permissions))}
                    : item
            )
            .filter((item) => !isGroup(item) || item.children.length > 0);
    }, [navigation, effectiveRoles, canAccess]);

    // Desktop collapse (icon-only rail) + controlled group expansion.
    const [collapsed, setCollapsed] = useState(false);
    const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
        const s = new Set<string>();
        visibleNavigation.forEach((item) => {
            if (isGroup(item) && item.children.some((c) => location.pathname.startsWith(c.to))) {
                s.add(item.name);
            }
        });
        return s;
    });
    const toggleGroup = (name: string) =>
        setOpenGroups((prev) => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });

    if (!authReady || !bizReady) {
        return <div className="p-6">Loading…</div>;
    }

    if (!user) {
        return <Navigate to="/" replace state={{from: location.pathname}}/>;
    }

    // Force select when needed (and avoid redirect loops)
    if (location.pathname !== "/business/select") {
        if (businesses.length === 0) return <Navigate to="/business/select" replace/>;
        if (!activeBusiness && businesses.length > 1) return <Navigate to="/business/select" replace/>;
    }

    const handleLogout = () => {
        logout();
        navigate("/", {replace: true});
    };

    const displayName = user?.name ?? user?.email ?? "Account";

    const SidebarNav = ({onNavigate, collapsed = false}: { onNavigate?: () => void; collapsed?: boolean }) => (
        <nav className="mt-2 flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-2">
                {visibleNavigation.map((item) => {
                    if (!isGroup(item)) {
                        return (
                            <li key={item.name}>
                                <NavLink
                                    to={item.to}
                                    end={item.end}
                                    onClick={onNavigate}
                                    title={collapsed ? item.name : undefined}
                                    className={({isActive}) => navLinkClass(isActive, collapsed)}
                                >
                                    <item.icon aria-hidden="true" className="size-6 shrink-0"/>
                                    {!collapsed && <span className="flex flex-1 items-center">{item.name}</span>}
                                    {!collapsed && item.badge}
                                </NavLink>
                            </li>
                        );
                    }

                    // Collapsed: icon-only button that re-expands the sidebar and opens this group.
                    if (collapsed) {
                        return (
                            <li key={item.name}>
                                <button
                                    type="button"
                                    title={item.name}
                                    onClick={() => {
                                        setCollapsed(false);
                                        setOpenGroups((prev) => new Set(prev).add(item.name));
                                    }}
                                    className="group flex w-full justify-center rounded-md p-2 text-sm/6 font-semibold text-gray-300 hover:bg-white/5 hover:text-white"
                                >
                                    <item.icon aria-hidden="true" className="size-6 shrink-0"/>
                                </button>
                            </li>
                        );
                    }

                    // Expanded: controlled group / dropdown in sidebar.
                    const open = openGroups.has(item.name);
                    return (
                        <li key={item.name}>
                            <button
                                type="button"
                                onClick={() => toggleGroup(item.name)}
                                className={cx(
                                    "w-full",
                                    "group flex items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                                    open ? "bg-white/5 text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"
                                )}
                            >
                                <item.icon aria-hidden="true" className="size-6 shrink-0"/>
                                <span className="flex-1 text-left">{item.name}</span>
                                <ChevronDownIcon
                                    className={cx("size-5 text-gray-400 transition", open && "rotate-180")}
                                    aria-hidden="true"
                                />
                            </button>

                            {open && (
                                <ul className="mt-1 space-y-1 pl-2">
                                    {item.children.map((child) => (
                                        <li key={child.name}>
                                            <NavLink
                                                to={child.to}
                                                end={child.end}
                                                onClick={onNavigate}
                                                className={({isActive}) =>
                                                    cx(
                                                        isActive
                                                            ? "bg-white/10 text-white"
                                                            : "text-gray-300 hover:bg-white/5 hover:text-white",
                                                        "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                                                    )
                                                }
                                            >
                                                <child.icon aria-hidden="true"
                                                            className="size-5 shrink-0 opacity-90"/>
                                                {child.name}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

    return (
        <div className="min-h-screen">
            {/* Mobile sidebar */}
            <Transition show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                    <TransitionChild
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-900/80"/>
                    </TransitionChild>

                    <div className="fixed inset-0 flex">
                        <TransitionChild
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
                                <div className="absolute top-0 left-full flex w-16 justify-center pt-5">
                                    <button
                                        type="button"
                                        onClick={() => setSidebarOpen(false)}
                                        className="-m-2.5 p-2.5 text-white hover:bg-white/10 rounded-md"
                                    >
                                        <span className="sr-only">Close sidebar</span>
                                        <XMarkIcon aria-hidden="true" className="size-6"/>
                                    </button>
                                </div>

                                <div
                                    className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4 ring-1 ring-white/10">
                                    <div className="flex h-16 shrink-0 items-center">
                                        <Link to="/" className="m-1.5" onClick={() => setSidebarOpen(false)}>
                                            <img src={logo} alt="NamEvents" className="mt-8 h-16 w-auto invert"/>
                                        </Link>
                                    </div>

                                    <SidebarNav onNavigate={() => setSidebarOpen(false)}/>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </Transition>

            {/* Desktop sidebar */}
            <div
                className={cx(
                    "hidden bg-gray-900 ring-1 ring-white/10 transition-[width] duration-300 lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col",
                    collapsed ? "lg:w-20" : "lg:w-72"
                )}>
                <div className={cx("flex grow flex-col gap-y-5 overflow-y-auto bg-black/10 pb-4", collapsed ? "px-3" : "px-6")}>
                    <div className="flex h-16 shrink-0 items-center">
                        {!collapsed && (
                            <Link to="/" className="m-1.5" aria-label="NamEvents">
                                <img src={logo} alt="NamEvents" className="mt-8 h-16 w-auto invert" loading="eager"
                                     decoding="async"/>
                                <span className="sr-only">NamEvents</span>
                            </Link>
                        )}
                    </div>

                    <SidebarNav collapsed={collapsed}/>
                </div>
            </div>

            {/* Main column */}
            <div className={cx("transition-[padding] duration-300", collapsed ? "lg:pl-20" : "lg:pl-72")}>
                {/* Top bar */}
                <div
                    className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8">
                    <button
                        type="button"
                        onClick={() => setSidebarOpen(true)}
                        className="-m-2.5 p-2.5 text-gray-700 hover:text-gray-900 lg:hidden"
                    >
                        <span className="sr-only">Open sidebar</span>
                        <Bars3Icon aria-hidden="true" className="size-6"/>
                    </button>

                    <button
                        type="button"
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                        aria-expanded={!collapsed}
                        className="-m-2.5 hidden p-2.5 text-gray-700 hover:text-gray-900 lg:inline-flex"
                    >
                        {collapsed
                            ? <ChevronDoubleRightIcon aria-hidden="true" className="size-6"/>
                            : <ChevronDoubleLeftIcon aria-hidden="true" className="size-6"/>}
                    </button>

                    <div aria-hidden="true" className="h-6 w-px bg-gray-900/10 lg:hidden"/>

                    <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">

                        <div className="grid flex-1 items-center grid-cols-1">
                            <Menu as="div" className="relative">
                                <MenuButton
                                    className="flex items-center gap-2 rounded-full px-3 py-1.5 hover:bg-gray-50">
                                    {activeBusiness && activeBusiness.imageUrl ? (
                                        <img src={activeBusiness.imageUrl} alt={activeBusiness.name}
                                             className="size-9 object-cover"/>
                                    ) : (
                                        <div
                                            className="flex size-9 items-center justify-center">
                                            <BuildingStorefrontIcon
                                                className="size-5 text-gray-400"/>
                                        </div>
                                    )}
                                    <span className="max-w-[18rem] truncate text-sm font-semibold text-gray-900">
                                        {activeBusiness?.name ?? "Select business"}
                                    </span>
                                    <ChevronDownIcon className="size-5 text-gray-400"/>
                                </MenuButton>

                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                >
                                    <MenuItems
                                        className="absolute left-0 z-10 mt-2.5 w-80 origin-top-left rounded-xl bg-white py-2 shadow-lg outline outline-gray-900/5"
                                    >
                                        <div className="px-3 pb-2 text-xs font-semibold text-gray-500">
                                            Your businesses
                                        </div>

                                        <div className="max-h-72 overflow-auto">
                                            {businesses.map((b) => (
                                                <MenuItem key={b.id}>
                                                    {({focus}) => (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                selectBusiness(b.id);
                                                            }}
                                                            className={cx(
                                                                "w-full px-3 py-2 text-left text-sm",
                                                                focus ? "bg-gray-50" : "",
                                                                activeBusiness?.id === b.id ? "font-semibold text-gray-900" : "text-gray-700"
                                                            )}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div
                                                                    className="size-9 shrink-0 overflow-hidden rounded-lg bg-gray-100 ring-1 ring-black/5">
                                                                    {b.imageUrl ? (
                                                                        <img src={b.imageUrl} alt=""
                                                                             className="size-9 object-cover"/>
                                                                    ) : (
                                                                        <div
                                                                            className="flex size-9 items-center justify-center">
                                                                            <BuildingStorefrontIcon
                                                                                className="size-5 text-gray-400"/>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="min-w-0 flex-1">
                                                                    <div className="truncate">
                                                                        {b.name}
                                                                    </div>
                                                                </div>

                                                                {activeBusiness?.id === b.id ? (
                                                                    <span
                                                                        className="text-xs text-gray-500">Active</span>
                                                                ) : null}
                                                            </div>
                                                        </button>
                                                    )}
                                                </MenuItem>
                                            ))}
                                        </div>
                                    </MenuItems>
                                </Transition>
                            </Menu>

                        </div>

                        <div className="flex items-center gap-x-4 lg:gap-x-6">

                            {/*<button type="button" className="-m-2.5 p-2.5 text-gray-500 hover:text-gray-500">*/}
                            {/*    <span className="sr-only">View notifications</span>*/}
                            {/*    <BellIcon aria-hidden="true" className="size-6"/>*/}
                            {/*</button>*/}

                            {/*<div aria-hidden="true" className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"/>*/}

                            <Menu as="div" className="relative">
                                <MenuButton className="relative flex items-center rounded-full focus:outline-none">
                                    <span className="sr-only">Open user menu</span>
                                    <img
                                        alt=""
                                        src={
                                            user?.avatarUrl ||
                                            `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=0D8ABC&color=fff`
                                        }
                                        className="size-8 rounded-full bg-gray-50 outline -outline-offset-1 outline-black/5"
                                    />
                                    <span className="hidden lg:flex lg:items-center">
                                        <span aria-hidden="true" className="ml-4 text-sm/6 font-semibold text-gray-900">
                                          {displayName}
                                        </span>
                                        <ChevronDownIcon aria-hidden="true" className="ml-2 size-5 text-gray-400"/>
                                    </span>
                                </MenuButton>

                                <MenuItems
                                    className="absolute right-0 z-10 mt-2.5 w-40 origin-top-right rounded-md bg-white py-2 shadow-lg outline outline-gray-900/5">
                                    <MenuItem>
                                        {({focus}) => (
                                            <Link
                                                to="/profile"
                                                className={cx("block px-3 py-1 text-sm/6 text-gray-900", focus && "bg-gray-50")}
                                            >
                                                Your profile
                                            </Link>
                                        )}
                                    </MenuItem>

                                    <MenuItem>
                                        {({focus}) => (
                                            <button
                                                type="button"
                                                onClick={handleLogout}
                                                className={cx(
                                                    "block w-full px-3 py-1 text-left text-sm/6 text-gray-900",
                                                    focus && "bg-gray-50"
                                                )}
                                            >
                                                Sign out
                                            </button>
                                        )}
                                    </MenuItem>
                                </MenuItems>
                            </Menu>
                        </div>
                    </div>
                </div>

                <main className="py-10">
                    <div className="px-4 sm:px-6 lg:px-8">
                        <Outlet/>
                    </div>
                </main>
            </div>

            <BackToTop/>
        </div>
    );
}